import { DatePipe, NgClass } from '@angular/common';
import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ApplicationService as AppApplicationService } from 'src/app/services/craftlink/application.service';
import { LocaleService, SUPPORTED_LOCALE } from 'src/app/services/locale.service';
import { MetaService } from 'src/app/services/meta.service';
import { ApplicationService } from 'src/app/services/openapi/api/api';
import { ActiveApplicationResponse, JobPostingResponse } from 'src/app/services/openapi/model/models';
import { ChangeLocaleBottomsheetComponent } from 'src/app/shared/components/change-locale-bottomsheet/change-locale-bottomsheet.component';
import {
  COUNTRIES_MAP,
  COUNTRY_SUBDIVISIONS,
  COUNTRY_SUBDIVISIONS_MAP,
  PCL_OPERATING_COUNTRIES,
} from 'src/app/shared/constants';
import { SharePostingComponent } from '../../shared/components/share-posting/share-posting.component';

@Component({
  selector: 'app-posting',
  templateUrl: './posting.component.html',
  styleUrls: ['./posting.component.scss'],
  imports: [NgClass, MatButtonModule, RouterLink, DatePipe],
})
export class PostingComponent implements OnInit, OnDestroy {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly title: Title = inject(Title);
  private readonly metaService: MetaService = inject(MetaService);
  private readonly msalService = inject(MsalService, { optional: true });
  private readonly localeService: LocaleService = inject(LocaleService);
  private readonly appApplicationService: AppApplicationService = inject(AppApplicationService);
  private readonly applicationService: ApplicationService = inject(ApplicationService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly jobNumber: string = '';
  private metaTags: HTMLMetaElement[] = [];
  private destroy$ = new Subject<void>();
  readonly breakpointService = inject(BreakpointService);
  posting: JobPostingResponse | null = null;
  activeApplication: ActiveApplicationResponse | undefined;
  countryName: string | undefined;
  provinceName: string | undefined;

  constructor() {
    if (this.route.snapshot.paramMap.has('id')) {
      this.jobNumber = this.route.snapshot.paramMap.get('id')!;
    }
  }

  ngOnInit(): void {
    this.posting = this.route.snapshot.data['posting'] as JobPostingResponse;
    this.setDisplayValues(this.posting);
    this.metaTags = this.metaService.addPostingMetaTags(this.posting);
    this.title.setTitle(`CraftLink ${this.posting.jobTitle}`);

    if (this.msalService && this.msalService.instance.getAllAccounts().length > 0) {
      this.checkForActiveApplication();
    }
  }

  private checkForActiveApplication() {
    this.applicationService.getApplicationByJobNumber(this.localeService.regionCode, this.jobNumber).subscribe({
      next: (activeApplication) => {
        this.activeApplication = activeApplication;
      },
      error: (err) => {
        if (err.status !== 404) {
          console.error(err);
        }
      },
    });
  }

  private setDisplayValues(posting: JobPostingResponse): void {
    this.countryName = COUNTRIES_MAP.get(posting.isoCountryCode);
    this.provinceName = COUNTRY_SUBDIVISIONS_MAP.get(posting.isoSubdivisionCode);
  }

  getPostingLocationDisplayString(jobPosting: JobPostingResponse): string {
    const strings = [
      jobPosting.city,
      COUNTRY_SUBDIVISIONS[jobPosting.isoSubdivisionCode] ?? jobPosting.isoSubdivisionCode,
      PCL_OPERATING_COUNTRIES[jobPosting.isoCountryCode] ?? jobPosting.isoCountryCode,
    ].filter((x) => x);
    return strings.join(', ');
  }

  openDialog(posting: JobPostingResponse) {
    this.dialog.open(SharePostingComponent, {
      data: posting,
      autoFocus: false,
    });
  }

  apply(posting: JobPostingResponse) {
    if (posting.isoCountryCode != this.localeService.regionCode) {
      const bottomSheetRef = this.bottomSheet.open(ChangeLocaleBottomsheetComponent, {
        data: {
          message: `It looks like you're applying for a job in ${COUNTRIES_MAP.get(
            posting.isoCountryCode
          )}, but your profile is set to ${COUNTRIES_MAP.get(
            this.localeService.regionCode
          )}. To proceed, please set up a new profile for ${COUNTRIES_MAP.get(posting.isoCountryCode)}.`,
        },
      });

      bottomSheetRef.afterDismissed().subscribe((result: boolean) => {
        if (result) {
          this.localeService.changeLocale(
            `${this.localeService.languageCode}-${posting.isoCountryCode.toUpperCase()}` as SUPPORTED_LOCALE,
            'profile'
          );
        } else return;
      });
    } else {
      this.appApplicationService.activeJobPosting = posting;
      this.router.navigate(['/profile']);
    }
  }

  ngOnDestroy(): void {
    this.title.setTitle('CraftLink');
    this.metaService.removePostingMetaTags(this.metaTags);
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
